import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "./use-organization";

export interface OnboardingData {
  id: string;
  organization_id: string;
  step_1_completed: boolean;
  step_1_completed_at: string | null;
  step_2_completed: boolean;
  step_2_completed_at: string | null;
  step_3_completed: boolean;
  step_3_completed_at: string | null;
  step_4_completed: boolean;
  step_4_completed_at: string | null;
  onboarding_completed: boolean;
  onboarding_completed_at: string | null;
  banner_collapsed: boolean;
  created_at: string;
  updated_at: string;
}

// Smart initialization: detect existing completion states for existing organizations
async function detectExistingCompletionStates(organizationId: string) {
  const now = new Date().toISOString();

  // Check Step 1: Organization profile filled (description, purpose, key_programs)
  const { data: org } = await supabase
    .from("organizations")
    .select("description, purpose, key_programs")
    .eq("id", organizationId)
    .single();

  const step1Complete = !!(
    org?.description?.trim() &&
    org?.purpose?.trim() &&
    org?.key_programs?.trim()
  );

  // Check Step 3: 5+ favorites
  const { count: favCount } = await supabase
    .from("organization_favorites")
    .select("*", { count: "exact", head: true })
    .eq("organization_id", organizationId);

  const step3Complete = (favCount || 0) >= 5;

  // Check Step 4: Has grants
  const { count: grantCount } = await supabase
    .from("grants")
    .select("*", { count: "exact", head: true })
    .eq("organization_id", organizationId);

  const step4Complete = (grantCount || 0) > 0;

  // Step 2: Auto-complete if they have grants (Option A - better UX for existing users)
  const step2Complete = step4Complete;

  // Check if all complete
  const allComplete =
    step1Complete && step2Complete && step3Complete && step4Complete;

  return {
    step_1_completed: step1Complete,
    step_1_completed_at: step1Complete ? now : null,
    step_2_completed: step2Complete,
    step_2_completed_at: step2Complete ? now : null,
    step_3_completed: step3Complete,
    step_3_completed_at: step3Complete ? now : null,
    step_4_completed: step4Complete,
    step_4_completed_at: step4Complete ? now : null,
    onboarding_completed: allComplete,
    onboarding_completed_at: allComplete ? now : null,
  };
}

export function useOnboarding() {
  const queryClient = useQueryClient();
  const { data: organization, isLoading: isOrgLoading } = useOrganization();
  const organizationId = organization?.id;

  // Fetch or create onboarding record
  const {
    data: onboardingData,
    isLoading: isOnboardingLoading,
    refetch,
  } = useQuery({
    queryKey: ["organization-onboarding", organizationId],
    queryFn: async (): Promise<OnboardingData | null> => {
      if (!organizationId) return null;

      // Try to fetch existing record
      const { data: existing, error } = await supabase
        .from("organization_onboarding")
        .select("*")
        .eq("organization_id", organizationId)
        .maybeSingle();

      if (error) {
        console.error("Error fetching onboarding:", error);
        return null;
      }

      // If record exists, return it
      if (existing) {
        return existing as OnboardingData;
      }

      // No record exists - detect current state and create with smart initialization
      const initialState = await detectExistingCompletionStates(organizationId);

      const { data: newRecord, error: insertError } = await supabase
        .from("organization_onboarding")
        .insert({
          organization_id: organizationId,
          ...initialState,
          banner_collapsed: false,
        })
        .select()
        .single();

      if (insertError) {
        console.error("Error creating onboarding record:", insertError);
        return null;
      }

      return newRecord as OnboardingData;
    },
    enabled: !!organizationId,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });

  // Mark step complete mutation
  const markStepCompleteMutation = useMutation({
    mutationFn: async (stepNumber: 1 | 2 | 3 | 4) => {
      if (!organizationId || !onboardingData) return;

      const stepField = `step_${stepNumber}_completed` as keyof OnboardingData;

      // Check if already complete (no regression)
      if (onboardingData[stepField] === true) return;

      const now = new Date().toISOString();
      const updateData: Record<string, unknown> = {
        [stepField]: true,
        [`step_${stepNumber}_completed_at`]: now,
        updated_at: now,
      };

      // Check if this completes all steps
      const willCompleteAll =
        (stepNumber === 1 || onboardingData.step_1_completed) &&
        (stepNumber === 2 || onboardingData.step_2_completed) &&
        (stepNumber === 3 || onboardingData.step_3_completed) &&
        (stepNumber === 4 || onboardingData.step_4_completed);

      if (willCompleteAll) {
        updateData.onboarding_completed = true;
        updateData.onboarding_completed_at = now;
      }

      const { error } = await supabase
        .from("organization_onboarding")
        .update(updateData)
        .eq("organization_id", organizationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["organization-onboarding", organizationId],
      });
    },
  });

  // Toggle banner collapsed state
  const toggleBannerCollapsedMutation = useMutation({
    mutationFn: async (collapsed: boolean) => {
      if (!organizationId) return;

      const { error } = await supabase
        .from("organization_onboarding")
        .update({
          banner_collapsed: collapsed,
          updated_at: new Date().toISOString(),
        })
        .eq("organization_id", organizationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["organization-onboarding", organizationId],
      });
    },
  });

  // Calculate progress
  const completedSteps = onboardingData
    ? [
        onboardingData.step_1_completed,
        onboardingData.step_2_completed,
        onboardingData.step_3_completed,
        onboardingData.step_4_completed,
      ].filter(Boolean).length
    : 0;

  const progressPercent = completedSteps * 25;

  return {
    onboardingData,
    isLoading: isOrgLoading || isOnboardingLoading,
    organizationId,
    completedSteps,
    progressPercent,
    markStepComplete: (step: 1 | 2 | 3 | 4) =>
      markStepCompleteMutation.mutate(step),
    toggleBannerCollapsed: (collapsed: boolean) =>
      toggleBannerCollapsedMutation.mutate(collapsed),
    refetch,
  };
}
